import { memo, useCallback } from "react";
import { Location, TransportData } from "@/types/trip";
import { Tooltip } from "@heroui/tooltip";
import { TrashIcon, PencilIcon, HomeIcon } from "@heroicons/react/24/outline";
import { getFlagImage } from "@/lib/general";
import { ArrowRightIcon, CirclePlusIcon, PlaneIcon } from "lucide-react";

interface LocationItemProps {
  location: Location;
  locations: Location[];
  index: number;
  isAdmin: boolean;
  transportData: TransportData;
  activeTransportButtonId: string | null;
  selectedLocationId: string | null;
  handleLocationClick: (location: Location) => void;
  handleTransportButtonClick: (location: Location) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  removeLocationById: (locationId: string) => void;
}

const LocationItem: React.FC<LocationItemProps> = memo(
  ({
    location,
    locations,
    index,
    isAdmin,
    activeTransportButtonId,
    selectedLocationId,
    handleLocationClick,
    handleTransportButtonClick,
    setIsModalOpen,
    removeLocationById,
  }) => {
    const isSelected =
      selectedLocationId !== null &&
      location.id === selectedLocationId &&
      activeTransportButtonId === null;
    const isFirstLocation = index === 0;
    const isSecondLocation = index === 1;
    const isLastLocation = index === locations.length - 1;

    const locationAbove = locations[index - 1] ?? locations[0];

    const handleDeleteLocation = useCallback(() => {
      removeLocationById(location.id);
    }, [location.id, removeLocationById]);

    return (
      <div className="tranLocSidebarContainer flex flex-col items-center justify-center">
        {index !== 0 && (
          <>
            <div
              className={`w-[1.5px] h-[6px] rounded-full ${
                activeTransportButtonId === location.id
                  ? "bg-indigo-500"
                  : "bg-neutral-400"
              }`}
            ></div>
            <Tooltip
              delay={500}
              showArrow={true}
              content={
                <div className="flex flex-row items-center justify-center space-x-2 px-3 py-0.5 font-light">
                  <p className="text-neutral-800 text-xs">
                    {locationAbove?.name}
                  </p>
                  <ArrowRightIcon className="w-3 h-3 text-neutral-800" />
                  <p className="text-neutral-800 text-xs">{location?.name}</p>
                </div>
              }
              placement="right"
              size="sm"
              radius="full"
              shadow="sm"
              offset={10}
            >
              <div className="py-1">
                <button
                  onClick={() => handleTransportButtonClick(location)}
                  className={`w-5 h-5 flex items-center justify-center rounded-full transition-all duration-200 group hover:scale-125
                ${
                  activeTransportButtonId === location.id
                    ? "bg-indigo-500"
                    : "bg-neutral-100 hover:bg-neutral-200"
                }`}
                >
                  <div
                    className={`flex items-center justify-center transition-transform duration-[600ms] ease-in-out origin-center
                  ${
                    activeTransportButtonId === location.id
                      ? "text-white rotate-[0.375turn]"
                      : "text-neutral-500 group-hover:text-neutral-700 rotate-[-0.125turn]"
                  }`}
                  >
                    <PlaneIcon
                      className="w-2.5 h-2.5"
                      fill="currentColor"
                      strokeWidth={1.5}
                    />
                  </div>
                </button>
              </div>
            </Tooltip>
            <div
              className={`w-[1.5px] h-[6px] rounded-full ${
                activeTransportButtonId === location.id
                  ? "bg-indigo-500"
                  : "bg-neutral-400"
              }`}
            ></div>
          </>
        )}
        <li
          className="tranLocSidebarContainerHandle locationSidebar w-full"
          {...(isSecondLocation ? { "data-tutorial": "location-item" } : {})}
        >
          <div
            className={`flex items-center w-full text-gray-900 hover:bg-neutral-200/60 rounded-full group p-2 transition duration-300 ease-in-out
            ${isSelected ? "" : "hover:shadow-sm"}`}
          >
            <button
              onClick={() => handleLocationClick(location)}
              className="locationBtn rounded-full group-hover:scale-[1.04] duration-300 ease-in-out relative"
            >
              <Tooltip
                delay={0}
                closeDelay={250}
                showArrow={true}
                placement="right"
                size="sm"
                radius="full"
                shadow="sm"
                offset={10}
                content={
                  <div className="relative flex items-center gap-3 px-3 py-2">
                    <span className="text-xs font-normal text-neutral-700">
                      {location.name}
                    </span>
                    <div className="w-1 h-1 bg-indigo-300 rounded-full"></div>
                    <div className="flex items-center gap-3">
                      <button
                        className="text-neutral-500 hover:text-rose-500 transition-all duration-200 flex items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLocation();
                        }}
                      >
                        <TrashIcon className="w-4 h-4" strokeWidth={1.5} />
                        <span className="text-xs ml-1.5 font-light">
                          Remove
                        </span>
                      </button>
                      <button
                        className="text-neutral-500 hover:text-indigo-500 transition-all duration-200 flex items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLocationClick(location);
                        }}
                      >
                        <PencilIcon className="w-4 h-4" strokeWidth={1.5} />
                        <span className="text-xs ml-1.5 font-light">Edit</span>
                      </button>
                    </div>
                  </div>
                }
              >
                {isFirstLocation ? (
                  <div className="relative">
                    <div className="h-8 w-8 2xl:w-9 2xl:h-9 rounded-full bg-white flex items-center justify-center hover:bg-neutral-50 ring-1 ring-neutral-300 transition-colors duration-200">
                      <HomeIcon
                        className="w-5 h-5 text-neutral-500"
                        strokeWidth={1.5}
                      />
                    </div>
                    <img
                      id="tranLocSidebarImage"
                      src={getFlagImage(location.address_components.country)}
                      alt="Location Icon"
                      className={`absolute -bottom-2 -right-2 h-5 w-5 2xl:-bottom-1.5 2xl:-right-1.5 2xl:w-5 2xl:h-5 rounded-full`}
                    />
                  </div>
                ) : (
                  <img
                    id="tranLocSidebarImage"
                    src={getFlagImage(location.address_components.country)}
                    alt="Location Icon"
                    className={`h-7 w-7 2xl:w-9 2xl:h-9 rounded-full
                    ${
                      isSelected
                        ? "ring-[1.5px] ring-offset-2 ring-slate-300"
                        : ""
                    }`}
                  />
                )}
              </Tooltip>
            </button>
          </div>
        </li>

        {isLastLocation && isAdmin && (
          <>
            <div className="w-[1.5px] h-[6px] rounded-full bg-neutral-400"></div>
            <li className="flex items-center justify-center w-full pt-1">
              <Tooltip
                delay={500}
                showArrow={true}
                content="Add new destination"
                placement="right"
                size="sm"
                radius="full"
                shadow="sm"
                offset={10}
              >
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="sidebarTripHoverClass transportButtonSidebar transition-all duration-300 ease-in-out
                  text-neutral-500 hover:scale-[1.10] hover:text-indigo-700 outline-none"
                  data-tutorial="add-destination"
                >
                  <CirclePlusIcon className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </Tooltip>
            </li>
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.location.id === nextProps.location.id &&
      prevProps.selectedLocationId === nextProps.selectedLocationId &&
      prevProps.activeTransportButtonId === nextProps.activeTransportButtonId &&
      prevProps.index === nextProps.index
    );
  }
);

LocationItem.displayName = "LocationItem";

export default LocationItem;
